import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Input, Button } from 'antd';
import { Check, X, Edit } from 'lucide-react';
import type { InputRef } from 'antd';

type Props = {
  value: string;
  onSave: (value: string) => Promise<void>;
  inputComponent?: React.ReactNode;
  viewComponent?: React.ReactNode;
  label?: string;
  disabled: boolean;
};

const InlineEditableField: React.FC<Props> = ({
  value,
  onSave,
  inputComponent,
  viewComponent,
  label,
  disabled
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(value);
  const inputRef = useRef<InputRef>(null);

  // Захист від подвійного виклику та blur після Enter/кнопки
  const savingRef = useRef(false);
  const skipBlurRef = useRef(false); // true якщо вже зберігаємо через Enter або кнопку

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  // Підтягувати зовнішнє value, якщо його змінили ззовні
  useEffect(() => {
    setEditedValue(value);
  }, [value]);

  const commit = useCallback(
    async (source: 'enter' | 'blur' | 'button') => {
      if (savingRef.current) return;

      // Якщо збереження ініційоване не blur — блокуємо наступний blur
      if (source !== 'blur') {
        skipBlurRef.current = true;
      } else {
        // Якщо blur прийшов одразу після Enter/кнопки — ігноруємо його
        if (skipBlurRef.current) {
          skipBlurRef.current = false;
          return;
        }
      }

      const next = editedValue.trim();

      // Нема змін або пусте => просто закриваємо редактор
      if (next === '' || next === value) {
        setEditedValue(value);
        setIsEditing(false);
        // Скидаємо прапорець пізніше, щоб не зловити одразу ще один blur
        Promise.resolve().then(() => (skipBlurRef.current = false));
        return;
      }

      try {
        savingRef.current = true;
        await onSave(next);
        setIsEditing(false);
      } finally {
        savingRef.current = false;
        // Скинути прапорець після мікротаску — щоб можливий blur уже не спрацював
        Promise.resolve().then(() => (skipBlurRef.current = false));
      }
    },
    [editedValue, onSave, value]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // щоб Enter не провокував додаткові побічні ефекти
      void commit('enter');
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditedValue(value);
    }
  };

  const handleSaveClick = () => {
    void commit('button');
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setEditedValue(value);
  };

  return (
    <div className="inline-editable-field">
      {label && <div className="inline-editable-label">{label}</div>}
      <div className="inline-editable-content group">
        {isEditing ? (
          <div className="inline-editable-input">
            {inputComponent || (
              <Input
                ref={inputRef}
                value={editedValue}
                onChange={(e) => setEditedValue(e.target.value)}
                onBlur={() => commit('blur')}
                onKeyDown={handleKeyDown}
                className="no-border-input"
              />
            )}
            <div className="inline-editable-actions">
              {/* ВАЖЛИВО: блокуємо blur при кліку на кнопки,
                  щоб не було подвійного збереження (blur -> onClick) */}
              <Button
                type="primary"
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleSaveClick}
                className="hover-effect"
                disabled={disabled}
              >
                <Check size={14} />
              </Button>
              <Button
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleCancelClick}
                className="hover-effect"
                disabled={disabled}
              >
                <X size={14} />
              </Button>
            </div>
          </div>
        ) : (
          <div
            className={`${disabled ? "" : "inline-editable-view hover-effect"}`}
            onClick={() => {
              if (!disabled) setIsEditing(true);
            }}
          >
            {viewComponent || <span>{value}</span>}
            <Button
              type="text"
              size="small"
              className="edit-button hover-effect"
              icon={<Edit size={14} />}
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) setIsEditing(true);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default InlineEditableField;
